import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AppState } from '../store/store';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { ApiUser } from '../api';
import { activateUser, deactivateUser, reset2faForUser, saveUser, unlockUser } from './userActions';
import * as Yup from 'yup';
import FormField from '../form/FormField';
import { FormikState } from 'formik/dist/types';
import {
  passwordMaximumLimit,
  passwordMinimumLimit2024,
  passwordMinimumLimitDevelopment,
} from '../common/validation/helper';
import { Button, SalvDropdown, SalvModal } from '@salvapp/ui-components';
import { showConfirmationModal } from '../confirmation-modal/confirmationModalReducer';
import { parseErrorMessage } from '../common/errors/helpers';
import { isDevelopment } from '../tracking/environment';

export type UserModalValues = ApiUser;

export interface UserModalProps {
  show: boolean;
  onComplete: (user?: ApiUser) => void;
  user: ApiUser | null;
}

export const UserModal = ({ user, show, onComplete }: UserModalProps) => {
  const dispatch = useDispatch();
  const roles = useSelector((state: AppState) => state.users.rolesForMultiSelect, shallowEqual);

  const misPasswordLength = isDevelopment() ? passwordMinimumLimitDevelopment.value : passwordMinimumLimit2024.value;
  const misPasswordMessage = isDevelopment()
    ? passwordMinimumLimitDevelopment.message
    : passwordMinimumLimit2024.message;

  const validationSchema = Yup.object().shape({
    id: Yup.number().notRequired(),
    email: Yup.string().email('Invalid email').required('Required'),
    password: Yup.string()
      .min(misPasswordLength, misPasswordMessage)
      .max(passwordMaximumLimit.value, passwordMaximumLimit.message),
    name: Yup.string().required('Required'),
  });

  const convertForSelect = (r: { name: string; id?: number }) => {
    return { label: r.name, key: String(r.id), value: String(r.id) };
  };

  const filterRolesForSelect = () => {
    return roles.map(convertForSelect);
  };

  const initialValues: UserModalValues =
    user !== null
      ? {
          ...user,
        }
      : {
          email: '',
          name: '',
          roles: [],
          password: undefined,
          organisation: undefined,
        };

  const initialFormikState: Partial<FormikState<UserModalValues>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  const submit = async (
    values: UserModalValues,
    { setStatus, setSubmitting, resetForm }: FormikHelpers<UserModalValues>
  ) => {
    try {
      await dispatch(saveUser(values));
      resetForm(initialFormikState);
      onComplete();
    } catch (e) {
      setStatus(await parseErrorMessage(e));
    }
    setSubmitting(false);
  };
  const onUnlockUser = async (user: ApiUser) => {
    if (user.id) {
      dispatch(
        showConfirmationModal({
          title: 'Are you sure?',
          description:
            'The user was locked because of too many failed login attempts. After unlocking the user can log in to Salv again.',
          confirmationText: 'Yes, unlock the user!',
          cancelText: 'No, keep the user locked',
          onConfirm: async () => {
            await unlockUser(user.id!);
            onComplete(user);
          },
        })
      );
    }
  };

  const onReset2FA = async (user: ApiUser) => {
    if (user.id) {
      dispatch(
        showConfirmationModal({
          title: 'Are you sure?',
          description:
            'This will remove 2FA configuration for the user, after this they will be able to login with JUST THE PASSWORD.',
          confirmationText: 'Yes, reset 2FA configuration!',
          cancelText: 'No, cancel the action',
          onConfirm: async () => {
            await reset2faForUser(user.id!);
            onComplete(user);
          },
        })
      );
    }
  };
  const onDeactivateUser = async (user: ApiUser) => {
    if (user.id) {
      dispatch(
        showConfirmationModal({
          title: 'Are you sure?',
          description: 'This user will be deactivated and cannot log in to Salv anymore.',
          confirmationText: 'Yes, deactivate the user!',
          cancelText: 'No, keep the user',
          onConfirm: async () => {
            await deactivateUser(user.id!);
            onComplete(user);
          },
        })
      );
    }
  };
  const onActivateUser = async (user: ApiUser) => {
    if (user.id) {
      dispatch(
        showConfirmationModal({
          title: 'Are you sure?',
          description: 'This user will be reactivated and can log in to Salv again.',
          confirmationText: 'Yes, reactivate the user!',
          cancelText: 'No, keep it deactivated',
          onConfirm: async () => {
            await activateUser(user.id!);
            onComplete(user);
          },
        })
      );
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={submit}
      initialValues={initialValues}
    >
      {({ isSubmitting, values, setFieldValue, resetForm }: FormikProps<UserModalValues>) => {
        const setRolesValue = (field: string, value: any) => {
          let roles: Record<string, any> = [];
          if (value) {
            roles = value.map((r: any) => {
              return { name: r.label, id: Number(r.value) };
            });
          }
          setFieldValue(field, roles);
        };

        const handleClose = () => {
          onComplete();
          resetForm();
        };

        return (
          <SalvModal show={show} onHide={handleClose}>
            <Form autoComplete='off'>
              <SalvModal.Header>{user ? 'Edit user' : 'Add user'} </SalvModal.Header>

              <SalvModal.Body>
                <FormField name='email' label='Email address' placeholder='Enter email' disabled={!!user} />
                <FormField name='name' label='Full name' placeholder='Enter full name' />
                {!user && (
                  <>
                    <FormField name='password' label='Password' type='password' />
                    <p>
                      The user will receive a welcome email with a link to set up their password when password left
                      empty.
                      <br />
                      <br />
                      <br />
                    </p>
                  </>
                )}
                <SalvDropdown
                  label={'Roles'}
                  options={filterRolesForSelect()}
                  isMulti
                  value={values.roles ? values.roles.map(convertForSelect) : undefined}
                  clearable={false}
                  renderedInBootstrapModal
                  onChange={(options) => {
                    setRolesValue('roles', options);
                  }}
                />
              </SalvModal.Body>

              <SalvModal.Footer>
                {user && (
                  <Button
                    disabled={isSubmitting}
                    onClick={() => onReset2FA(user)}
                    className='right-button'
                    variant='outline-danger'
                  >
                    Remove 2FA
                  </Button>
                )}
                {user && user.locked && (
                  <Button
                    disabled={isSubmitting}
                    onClick={() => onUnlockUser(user)}
                    className='right-button'
                    variant='outline-danger'
                  >
                    Unlock
                  </Button>
                )}
                {user && !user.inactive && (
                  <Button
                    disabled={isSubmitting}
                    onClick={() => onDeactivateUser(user)}
                    className='right-button'
                    variant='outline-danger'
                  >
                    Deactivate
                  </Button>
                )}
                {user && user.inactive && (
                  <Button
                    disabled={isSubmitting}
                    onClick={() => onActivateUser(user)}
                    className='right-button'
                    variant='outline-danger'
                  >
                    Reactivate
                  </Button>
                )}
                <Button variant='secondary' onClick={handleClose}>
                  Close
                </Button>
                <Button disabled={isSubmitting} type='submit' variant='primary'>
                  Save
                </Button>
              </SalvModal.Footer>
            </Form>
          </SalvModal>
        );
      }}
    </Formik>
  );
};

export default UserModal;
